import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Layout } from '../../components/Layout/Layout'
import api from '../../api/axios'
import Icon from '../../design/icons'
import { Sparkline, RevenueChart, PartnerBalances, StatusDonut, fmtCurEUR } from '../../design/charts'
import { exportRowsAsCsv } from '../../utils/csv'
import { T, useContent } from '../../content/ContentProvider'
import { renderInline } from '../../content/markdown'
import {
  REVENUE_SERIES_DEMO,
  PARTNER_BALANCES_DEMO,
  STATUS_DEMO,
  ACTIVITY_DEMO,
} from '../../design/demoData'

// ── KPI card matching design ─────────────────────────────────────────────────
function Kpi({ label, value, dir, delta, sub, accent, spark }) {
  return (
    <div className={'kpi ' + (accent ? 'kpi--accent' : '')}>
      <div className="kpi__label">{label}</div>
      <div className="kpi__value">{value}</div>
      <div className="kpi__sub">
        {dir && (
          <span className={'delta delta--' + (dir === 'up' ? 'up' : dir === 'down' ? 'down' : 'flat')}>
            {dir === 'up' ? '↑' : dir === 'down' ? '↓' : '—'} {delta}
          </span>
        )}
        {dir && sub && <span>·</span>}
        {sub && <span>{sub}</span>}
      </div>
      {spark && (
        <div className="kpi__chart">
          <Sparkline data={spark} color="var(--ht-gold-500)" />
        </div>
      )}
    </div>
  )
}

// ── Activity feed ────────────────────────────────────────────────────────────
function ActivityFeed({ items }) {
  const iconMap = { ok: 'check', warn: 'alert', err: 'close', info: 'info' }
  return (
    <div className="feed">
      {items.map((it, i) => (
        <div key={i} className="feed-item">
          <div className={'feed-item__icon feed-item__icon--' + it.kind}>{Icon[iconMap[it.kind]]({ s: 11 })}</div>
          <div className="feed-item__body">
            {/* Rendered as React nodes, not HTML. This title is either a
                description from the API or an administrator-editable catalogue
                string; piping either into an HTML sink would hand both a way to
                inject markup, and would break the guarantee the content system
                is built on. renderInline still supports **bold** and links. */}
            <div className="feed-item__title">{renderInline(it.title, 'feed-' + i)}</div>
            <div className="feed-item__sub">{it.sub}</div>
          </div>
          <div className="feed-item__time">{it.time}</div>
        </div>
      ))}
    </div>
  )
}

export function Dashboard() {
  const { t } = useContent()
  const navigate = useNavigate()
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalUsers: 0,
    totalPartnerships: 0,
    totalTransactions: 0,
    recentActivity: [],
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [period, setPeriod] = useState('30d')

  const exportSnapshot = () => {
    const cols = [
      { key: 'metric', label: t('dashboard.export.column.metric') },
      { key: 'value', label: t('dashboard.export.column.value') },
    ]
    const rows = [
      { metric: t('dashboard.export.row.companies'), value: stats.totalCompanies || 0 },
      { metric: t('dashboard.export.row.partnerships'), value: stats.totalPartnerships || 0 },
      { metric: t('dashboard.export.row.transactions'), value: stats.totalTransactions || 0 },
      { metric: t('dashboard.export.row.users'), value: stats.totalUsers || 0 },
    ]
    exportRowsAsCsv(rows, cols, 'mas-dashboard-snapshot')
  }

  useEffect(() => {
    let mounted = true
    api
      .get('/api/admin/dashboard')
      .then((r) => {
        if (mounted) setStats(r.data)
      })
      .catch((err) => {
        if (mounted) setError(err.response?.data?.message || t('dashboard.error.load'))
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [])

  // KPIs built from real data — values are real, sparklines are placeholder shape
  const fmt = (n) => n.toLocaleString('en-US')
  const kpis = [
    {
      label: t('dashboard.kpi.companies.label'),
      value: fmt(stats.totalCompanies || 0),
      dir: stats.totalCompanies > 0 ? 'up' : 'flat',
      delta: stats.totalCompanies > 0 ? t('dashboard.kpi.companies.delta') : '—',
      sub: t('dashboard.kpi.companies.sub'),
      spark: [4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, stats.totalCompanies || 4],
    },
    {
      label: t('dashboard.kpi.partnerships.label'),
      value: fmt(stats.totalPartnerships || 0),
      dir: 'flat',
      delta: t('dashboard.kpi.partnerships.delta'),
      sub: stats.totalPartnerships > 0 ? t('dashboard.kpi.partnerships.sub') : t('dashboard.kpi.partnerships.subEmpty'),
      spark: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, stats.totalPartnerships || 0],
    },
    {
      label: t('dashboard.kpi.transactions.label'),
      value: fmt(stats.totalTransactions || 0),
      dir: 'flat',
      delta: t('dashboard.kpi.transactions.delta'),
      sub: stats.totalTransactions > 0 ? t('dashboard.kpi.transactions.sub') : t('dashboard.kpi.transactions.subEmpty'),
      spark: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, stats.totalTransactions || 0],
    },
    {
      label: t('dashboard.kpi.users.label'),
      value: fmt(stats.totalUsers || 0),
      dir: 'up',
      delta: t('dashboard.kpi.users.delta'),
      sub: t('dashboard.kpi.users.sub'),
      spark: [1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3, stats.totalUsers || 3],
    },
  ]

  const activity = (stats.recentActivity && stats.recentActivity.length > 0) ? stats.recentActivity.map((a) => ({
    kind: 'info',
    title: a.description || t('dashboard.activity.fallbackTitle'),
    sub: a.timestamp || '',
    time: a.timestamp ? new Date(a.timestamp).toLocaleTimeString().slice(0, 5) : '',
  })) : ACTIVITY_DEMO

  return (
    <Layout crumb={t('dashboard.crumb')}>
      <div className="view">
        <div className="view__head">
          <div className="page-head">
            <div>
              <h1 className="page-head__h"><T k="dashboard.title" /></h1>
              <div className="page-head__sub">
                <T
                  k="dashboard.head.sub"
                  vars={{ date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) }}
                />
              </div>
            </div>
            <div className="page-head__actions">
              <button className="btn" onClick={() => navigate('/analytics')} title={t('dashboard.action.periodTitle')}>
                {Icon.calendar({ s: 12 })} <T k="dashboard.action.period" /> {Icon.chevronDown({ s: 10 })}
              </button>
              <button className="btn" onClick={exportSnapshot} title={t('dashboard.action.exportTitle')}>
                {Icon.download({ s: 12 })} <T k="dashboard.action.export" />
              </button>
              <button className="btn btn--primary" onClick={() => navigate('/reports')} title={t('dashboard.action.newReportTitle')}>
                {Icon.upload({ s: 12 })} <T k="dashboard.action.newReport" />
              </button>
            </div>
          </div>
        </div>

        <div className="view__body thin-scroll">
        {error && (
          <div
            className="status-msg status-msg--err"
            role="alert"
            aria-live="polite"
            style={{ marginBottom: 14 }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div className="ngrs-skeleton" style={{ height: 100, marginBottom: 14, borderRadius: 'var(--r-lg)' }} />
        ) : (
          <div className="kpi-grid">
            {kpis.map((k, i) => (
              <Kpi key={i} {...k} accent={i === 0} />
            ))}
          </div>
        )}

        <div className="grid-2">
          <div className="card">
            <div className="card__head">
              <div>
                <div className="card__title"><T k="dashboard.revenue.title" /></div>
                <div className="card__sub"><T k="dashboard.revenue.sub" /></div>
              </div>
              <div className="card__actions">
                {['14d', '30d', '90d'].map((p) => (
                  <button
                    key={p}
                    className="btn btn--ghost btn--sm"
                    style={period === p ? { background: 'var(--surface-2)', color: 'var(--text)' } : undefined}
                    onClick={() => setPeriod(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div className="card__body">
              <RevenueChart series={REVENUE_SERIES_DEMO} />
            </div>
          </div>

          <div className="card">
            <div className="card__head">
              <div>
                <div className="card__title"><T k="dashboard.status.title" /></div>
                <div className="card__sub"><T k="dashboard.status.sub" /></div>
              </div>
            </div>
            <div className="card__body">
              <StatusDonut data={STATUS_DEMO} />
            </div>
          </div>
        </div>

        <div className="grid-2">
          <div className="card">
            <div className="card__head">
              <div>
                <div className="card__title"><T k="dashboard.partners.title" /></div>
                <div className="card__sub"><T k="dashboard.partners.sub" /></div>
              </div>
              <button className="btn btn--ghost btn--sm" onClick={() => navigate('/companies')}><T k="dashboard.partners.viewAll" /> {Icon.chevronRight({ s: 11 })}</button>
            </div>
            <div className="card__body">
              <PartnerBalances data={PARTNER_BALANCES_DEMO} />
            </div>
          </div>

          <div className="card">
            <div className="card__head">
              <div>
                <div className="card__title"><T k="dashboard.activity.title" /></div>
                <div className="card__sub">
                  <T
                    k="dashboard.activity.sub"
                    vars={{
                      state: stats.recentActivity?.length > 0
                        ? t('dashboard.activity.state.live')
                        : t('dashboard.activity.state.demo'),
                    }}
                  />
                </div>
              </div>
              <button className="btn btn--ghost btn--sm" onClick={() => navigate('/analytics')}><T k="dashboard.activity.allEvents" /></button>
            </div>
            <div className="card__body card__body--flush">
              <ActivityFeed items={activity} />
            </div>
          </div>
        </div>
        </div>
      </div>
    </Layout>
  )
}
